// ─── THE PLAYED ARCHIVE AS CANON ─────────────────────────────────────────────
//
// 100,213 embedded Discord messages — ten years of the two writers playing
// these characters — reached a prompt only as VOICE samples, which the prompt
// forbids treating as fact. The knowledge maps of 2026-08-12 and 2026-10-05
// both ranked this the largest unopened source; match_archive_lore existed
// and was called from nowhere.
//
// This section retrieves the passages most relevant to the CURRENT scene
// (the same scene embedding the linked-memory and voice lookups already
// share — no extra embed call) from the channels where this character was
// actually played. CANON ROUTES ONLY: CHARACTER_ROUTES marks each played
// channel canon / route / au, and an AU passage must never walk into a canon
// scene (the 08-12 ruling on alternate_routes). Excluded authors (friends,
// bots) never surface — the RPC filters by writer_accounts.
//
// It is labeled as HISTORY, in the dynamic (per-turn) half, after the
// linked-storyline memory: the story above it outranks it where they differ.
import { supabase } from './supabaseClient.js';
import { CHARACTER_ROUTES } from './characterRoutes.js';

export const ARCHIVE_LORE_LIMIT = 6;
export const ARCHIVE_LORE_MIN_LENGTH = 120;
const PASSAGE_CHARS = 520;

/** The Discord channels this character was played in — canon routes only, never AU. */
export function archiveChannelsFor(charId) {
  const info = CHARACTER_ROUTES[charId];
  if (!info) return [];
  const out = new Set();
  if (info.canonRoute) out.add(info.canonRoute);
  for (const c of (Array.isArray(info.canonRoutes) ? info.canonRoutes : [])) if (c) out.add(c);
  for (const r of (Array.isArray(info.routes) ? info.routes : [])) {
    if (!r?.channel) continue;
    if (r.kind === 'au' || r.kind === 'ooc') continue; // never AU; OOC chatter is not lore
    out.add(r.channel);
  }
  return [...out];
}

/**
 * THIS THREAD'S ROUTE (2026-10-12). Haley, choosing the route a thread
 * continues: "Make sure it's right it should be from their first route ever"
 * → "I like the one that leads them to the children eventually" → #alistair-
 * reboot2, which the generated map files as AU, so archiveChannelsFor() could
 * never surface it. A thread that continues a specific played route names it
 * in story_preferences.canon_route; that one room is then the archive for the
 * thread, whatever the map calls it, and the other routes stay out, because
 * they contradict it (a different first night, a different meeting).
 * The picker offers every room the character was played in except OOC.
 */
export function routeOptionsFor(charId) {
  const info = CHARACTER_ROUTES[charId];
  if (!info) return [];
  const seen = new Set();
  const out = [];
  for (const r of (Array.isArray(info.routes) ? info.routes : [])) {
    if (!r?.channel || r.kind === 'ooc' || seen.has(r.channel)) continue;
    seen.add(r.channel);
    out.push({ channel: r.channel, kind: r.kind || 'route', passages: Number(r.passages || 0), first: r.first || '', last: r.last || '' });
  }
  if (info.canonRoute && !seen.has(info.canonRoute)) out.unshift({ channel: info.canonRoute, kind: 'canon', passages: 0, first: '', last: '' });
  return out;
}

/** Label for the thread-route picker: "#room (kind, N passages · first → last)". Pure. */
export function routeOptionLabel(r) {
  const kind = r.kind === 'canon' ? 'canon' : r.kind === 'au' ? 'AU' : 'route';
  const span = r.first ? ` · ${String(r.first).slice(0, 7)}${r.last && r.last !== r.first ? ` → ${String(r.last).slice(0, 7)}` : ''}` : '';
  return `#${r.channel} (${kind}${r.passages ? `, ${r.passages} passages` : ''}${span})`;
}

/** 'canon' | 'route' | 'au' | null — what the map calls this room for this character. */
export function routeKindFor(charId, channel) {
  const info = CHARACTER_ROUTES[charId];
  const ch = typeof channel === 'string' ? channel.trim() : '';
  if (!info || !ch) return null;
  if (info.canonRoute === ch) return 'canon';
  const r = (Array.isArray(info.routes) ? info.routes : []).find(x => x?.channel === ch);
  return r ? (r.kind || 'route') : null;
}

/** Character ids this character shared that room with (the route's own cast), [] when unknown. */
export function routeCastFor(charId, channel) {
  const info = CHARACTER_ROUTES[charId];
  const ch = typeof channel === 'string' ? channel.trim() : '';
  const r = (Array.isArray(info?.routes) ? info.routes : []).find(x => x?.channel === ch);
  return Array.isArray(r?.castWith) ? r.castWith.filter(Boolean) : [];
}

/** Route options for a ROOM with several AI characters (channels, group chats): the union, passages summed. */
export function routeOptionsForMany(ids) {
  const rank = { canon: 0, route: 1, au: 2 };
  const byChannel = new Map();
  for (const id of (Array.isArray(ids) ? ids : [])) {
    for (const o of routeOptionsFor(id)) {
      const cur = byChannel.get(o.channel);
      if (!cur) { byChannel.set(o.channel, { ...o }); continue; }
      cur.passages += o.passages;
      if ((rank[o.kind] ?? 1) < (rank[cur.kind] ?? 1)) cur.kind = o.kind;
      if (o.first && (!cur.first || o.first < cur.first)) cur.first = o.first;
      if (o.last && (!cur.last || o.last > cur.last)) cur.last = o.last;
    }
  }
  return [...byChannel.values()].sort((a, b) => b.passages - a.passages);
}

/** The channels to retrieve from: the thread's chosen route alone when it names one, else the map's canon routes. */
export function channelsForThread(charId, canonRoute = null) {
  const chosen = typeof canonRoute === 'string' ? canonRoute.trim() : '';
  if (chosen) return [chosen];
  return archiveChannelsFor(charId);
}

/** Render retrieved passages. Pure, byte-stable for the same rows. */
export function formatArchiveLoreForPrompt(rows, charName = '', chosenRoute = '', chosenKind = null) {
  const list = (rows || []).filter(r => r && String(r.content || '').trim());
  if (!list.length) return '';
  const lines = list.map(r => {
    const when = r.message_timestamp ? String(r.message_timestamp).slice(0, 10) : '';
    const text = String(r.content).replace(/\s+/g, ' ').trim();
    const clipped = text.length > PASSAGE_CHARS ? text.slice(0, PASSAGE_CHARS) + '…' : text;
    return `• [${r.channel_name || 'archive'}${when ? ` · ${when}` : ''}] ${r.author || 'someone'}: ${clipped}`;
  });
  const route = typeof chosenRoute === 'string' ? chosenRoute.trim() : '';
  if (route) {
    // "We like AUs too" (Haley, 2026-10-12): an AU route the writers chose is
    // the world of this thread, not a lesser copy of canon.
    const auLine = chosenKind === 'au'
      ? `#${route} is an ALTERNATE UNIVERSE. Its premise, its rules and its relationship state are the truth here even where they differ from your canon route; blood ties and species still hold.`
      : '';
    return [
      `FROM THE PLAYED ARCHIVE — passages from #${route}, the route THIS thread continues (the writer chose it), chosen for their relevance to the current scene:`,
      ...(auLine ? [auLine] : []),
      ...lines,
      `This thread continues THAT route. Where it and any other route, archive passage or profile line disagree about what happened between you (how you met, your first night, who you were to each other), this route is the truth and the rest did not happen here. These are HISTORY and reference, never the present scene; nothing in them is happening now, and where the story above and a passage disagree about the present, the story above wins.`,
    ].join('\n');
  }
  return [
    `FROM THE PLAYED ARCHIVE — passages from the roleplay in which ${charName || 'you'} ${charName ? 'was' : 'were'} actually played, drawn from your canon routes only, chosen for their relevance to this scene:`,
    ...lines,
    'These are HISTORY and reference, never the present scene. Use them for what happened, how people spoke to you, and how this world works. Nothing in them is happening now; where the story above and these passages differ, the story above wins.',
  ].join('\n');
}

/**
 * The archive section for one character and one scene.
 * @param {object} opts { char, sceneEmbedding (number[] or pg literal), limit, minLength, canonRoute (this thread's chosen route, optional) }
 */
export async function buildArchiveLoreSection({ char, sceneEmbedding, limit = ARCHIVE_LORE_LIMIT, minLength = ARCHIVE_LORE_MIN_LENGTH, canonRoute = null } = {}) {
  try {
    if (!char?.id || !sceneEmbedding) return '';
    const channels = channelsForThread(char.id, canonRoute);
    if (!channels.length) return '';
    const vec = Array.isArray(sceneEmbedding) ? '[' + sceneEmbedding.join(',') + ']' : String(sceneEmbedding);
    const { data, error } = await supabase.rpc('match_archive_lore', {
      p_query_embedding: vec,
      p_limit: limit,
      p_min_length: minLength,
      p_channels: channels,
    });
    if (error || !Array.isArray(data)) return '';
    const chosen = channels.length === 1 && canonRoute ? channels[0] : '';
    return formatArchiveLoreForPrompt(data, char.name, chosen, chosen ? routeKindFor(char.id, chosen) : null);
  } catch {
    return '';
  }
}
